import { useState, useEffect } from "react";
import "./css/adminstoreinfo.css";
import StoreInfoModal from "./modal/StoreInfoModal";
import { getAllStores, getStoreDetail, searchStores, getWithdrawalStores, unsuspendStore, immediateWithdrawStore } from "../../utils/adminStoreService";
import { downloadStoreExcel } from "../../utils/adminExcelService";
import Pagination from "../common/Pagination";
import { formatDate } from "../../utils/commonService";

const AdminStoreInfo = () => {
  const [activeTab, setActiveTab] = useState("ACTIVE");
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeList, setStoreList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 10;

  const [isSearchMode, setIsSearchMode] = useState(false);

  const [sortColumn, setSortColumn] = useState('approvedDateTime');
  const [sortDirection, setSortDirection] = useState('desc');

  const [filters, setFilters] = useState({
    approvalStatus: "",
    bizType: "",
    storeType: "all",
    searchType: "all",
    searchTerm: "",
  });

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async (page = 0, col = sortColumn, dir = sortDirection) => {
    setIsLoading(true);
    const result = await getAllStores(page, pageSize, col, dir);

    if (result.success && result.data) {
      setStoreList(result.data.content || []);
      setCurrentPage(result.data.number || 0);
      setTotalPages(result.data.totalPages || 0);
      setTotalElements(result.data.totalElements || 0);
      setIsSearchMode(false);
    } else {
      console.error(result.error);
      setStoreList([]);
      setTotalPages(0);
      setTotalElements(0);
      if (result.error) alert(result.error);
    }
    setIsLoading(false);
  };

  const loadWithdrawalStores = async (page = 0, col = 'withdrawalRequestTime', dir = 'desc') => {
    setIsLoading(true);
    const result = await getWithdrawalStores(page, pageSize, col, dir);

    if (result.success && result.data) {
      setStoreList(result.data.content || []);
      setCurrentPage(result.data.number || 0);
      setTotalPages(result.data.totalPages || 0);
      setTotalElements(result.data.totalElements || 0);
      setIsSearchMode(false);
    } else {
      setStoreList([]);
      setTotalPages(0);
      setTotalElements(0);
      if (result.error) alert(result.error);
    }
    setIsLoading(false);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(0);
    setIsSearchMode(false);
    setFilters({ approvalStatus: "", bizType: "", storeType: "all", searchType: "all", searchTerm: "" });
    if (tab === "WITHDRAWAL") {
      setSortColumn('withdrawalRequestTime');
      loadWithdrawalStores(0, 'withdrawalRequestTime', 'desc');
    } else {
      setSortColumn('approvedDateTime');
      loadStores(0, 'approvedDateTime', sortDirection);
    }
  };

  const handleSearchInternal = async (page = 0, col = sortColumn, dir = sortDirection) => {
    const searchParams = {
      ...(filters.approvalStatus && { approvalStatus: filters.approvalStatus }),
      ...(filters.bizType && filters.bizType !== "all" && { bizType: filters.bizType }),
      ...(filters.searchType && { searchType: filters.searchType }),
      ...(filters.searchTerm && { searchTerm: filters.searchTerm }),
    };

    setIsLoading(true);
    const result = await searchStores(searchParams, page, pageSize, col, dir);
    if (result.success && result.data) {
      setStoreList(result.data.content || []);
      setCurrentPage(result.data.number || 0);
      setTotalPages(result.data.totalPages || 0);
      setTotalElements(result.data.totalElements || 0);
      setIsSearchMode(true);
    } else {
      setStoreList([]);
      setTotalPages(0);
      setTotalElements(0);
      if (result.error) alert(result.error);
    }
    setIsLoading(false);
  };

  const handlePageChange = (page) => {
    if (activeTab === "WITHDRAWAL") {
      loadWithdrawalStores(page, sortColumn, sortDirection);
    } else {
      isSearchMode ? handleSearchInternal(page, sortColumn, sortDirection) : loadStores(page, sortColumn, sortDirection);
    }
  };

  const handleSearch = () => {
    setCurrentPage(0);
    handleSearchInternal(0);
  };

  const handleReset = () => {
    setFilters({ approvalStatus: "", bizType: "", storeType: "all", searchType: "all", searchTerm: "" });
    setCurrentPage(0);
    loadStores(0);
  };

  const handleCancelWithdrawal = async (store) => {
    if (!window.confirm(`[${store.storeNm}] 가맹점 탈퇴 철회를 진행하시겠습니까?`)) return;
    const result = await unsuspendStore(store.storeCode);
    if (result.success) {
      alert("탈퇴 철회가 완료되었습니다.");
      loadWithdrawalStores(currentPage);
    } else {
      alert(result.error || "탈퇴 철회 처리에 실패했습니다.");
    }
  };

  const handleImmediateWithdrawal = async (store) => {
    if (!window.confirm(`[${store.storeNm}] 탈퇴 처리하시겠습니까? 모든 데이터가 삭제되며 복구할 수 없습니다.`)) return;
    const result = await immediateWithdrawStore(store.storeCode);
    if (result.success) {
      alert("탈퇴 처리가 완료되었습니다.");
      loadWithdrawalStores(currentPage);
    } else {
      alert(result.error || "탈퇴 처리에 실패했습니다.");
    }
  };

  const handleOpenModal = async (store) => {
    try {
      const result = await getStoreDetail(store.storeCode);
      if (result.success) {
        setSelectedStore({ ...result.data, withdrawalRequestTime: store.withdrawalRequestTime });
        setIsDetailOpen(true);
      } else {
        alert(result.error);
      }
    } catch (error) {
      console.error("모달 오픈 중 오류:", error);
      alert("가맹점 정보 조회 실패");
    }
  };

  const handleCloseModal = () => {
    setIsDetailOpen(false);
    setSelectedStore(null);
  };

  const handleSort = (column) => {
    const newDir = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortColumn(column);
    setSortDirection(newDir);
    if (activeTab === "WITHDRAWAL") {
      loadWithdrawalStores(0, column, newDir);
    } else {
      loadStores(0, column, newDir);
    }
  };

  // const handleUpdate = () => {
  //   // 수정 후 목록 새로고침
  //   if (isSearchMode) {
  //     handleSearchInternal(currentPage);
  //   } else {
  //     loadStores(currentPage);
  //   }
  // };

  const renderSortTh = (label, col) => (
    <th
      key={col}
      className={`adminstoreinfo-sortable${sortColumn === col ? ' adminstoreinfo-sort-active' : ''}`}
      onClick={() => handleSort(col)}
    >
      {label}
      <span className="adminstoreinfo-sort-icon">
        {sortColumn === col ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </th>
  );

  return (
    <div className="adminstoreinfo-container">
      <div className="adminstoreinfo-wrap">
        <div className="adminstoreinfo-tab-navigation">
          <button
            className={`adminstoreinfo-tab-btn ${activeTab === "ACTIVE" ? "adminstoreinfo-tab-active" : ""}`}
            onClick={() => handleTabChange("ACTIVE")}
          >
            활성
          </button>
          <button
            className={`adminstoreinfo-tab-btn ${activeTab === "WITHDRAWAL" ? "adminstoreinfo-tab-active" : ""}`}
            onClick={() => handleTabChange("WITHDRAWAL")}
          >
            탈퇴 요청
          </button>
        </div>

        {activeTab === "ACTIVE" && (
          <div className="adminstoreinfo-filter-wrap">
            <div className="adminstoreinfo-filter-row storeinfo-search-row">
              <div className="adminstoreinfo-search-group">
                <select
                  name="search-type"
                  id="search-type"
                  className="adminstoreinfo-select-type"
                  value={filters.searchType}
                  onChange={(e) => setFilters({...filters, searchType: e.target.value})}
                >
                  <option value="all">전체</option>
                  <option value="storeName">가맹점명</option>
                  <option value="storeCode">가맹코드</option>
                  <option value="ownerName">점주명</option>
                </select>
                <input
                  type="text"
                  className="adminstoreinfo-input"
                  placeholder="검색어를 입력하세요"
                  value={filters.searchTerm}
                  onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
                />
                <button className="adminstoreinfo-search-btn" onClick={handleSearch}>검색</button>
                <button className="adminstoreinfo-reset-btn" onClick={handleReset}>초기화</button>
              </div>
            </div>
          </div>
        )}

        <div className="adminstoreinfo-table-wrap">
          <div className="adminstoreinfo-table-header">
            <div className="adminstoreinfo-table-info">
              총 <span className="adminstoreinfo-count">{totalElements}</span>건
            </div>
            {activeTab === "ACTIVE" && (
              <button
                className="adminstoreinfo-excel-btn"
                onClick={async () => {
                  setExcelLoading(true);
                  const result = await downloadStoreExcel();
                  setExcelLoading(false);
                  if (!result.success) alert(result.error);
                }}
                disabled={excelLoading}
              >
                {excelLoading ? "다운로드 중..." : "엑셀 다운로드"}
              </button>
            )}
          </div>

          {activeTab === "WITHDRAWAL" ? (
            <table className="adminstoreinfo-table">
              <thead>
                <tr>
                  {["가맹코드", "가맹점명", "점주명", "연락처", "탈퇴 요청일"].map((label) => (
                    <th key={label}>{label}</th>
                  ))}
                  <th style={{ width: "220px" }}>관리</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan="6" className="adminstoreinfo-empty-cell">로딩 중...</td></tr>
                ) : storeList.length === 0 ? (
                  <tr><td colSpan="6" className="adminstoreinfo-empty-cell">가맹점이 없습니다.</td></tr>
                ) : storeList.map((store) => (
                  <tr key={store.storeCode}>
                    <td>{store.storeCode}</td>
                    <td>{store.storeNm}</td>
                    <td>{store.ownerNm}</td>
                    <td>{store.ownerTelNo}</td>
                    <td>{formatDate(store.withdrawalRequestTime)}</td>
                    <td>
                      <button
                        className="adminstoreinfo-btn adminstoreinfo-btn-detail"
                        onClick={() => handleOpenModal(store)}
                      >
                        보기
                      </button>
                      <button
                        className="adminstoreinfo-btn adminstoreinfo-btn-cancel-withdrawal"
                        onClick={() => handleCancelWithdrawal(store)}
                      >
                        탈퇴 취소
                      </button>
                      <button
                        className="adminstoreinfo-btn adminstoreinfo-btn-immediate-withdrawal"
                        onClick={() => handleImmediateWithdrawal(store)}
                      >
                        즉시 탈퇴
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="adminstoreinfo-table adminstoreinfo-table-fixed">
              <thead>
                <tr>
                  {renderSortTh("가맹코드", "storeCode")}
                  {renderSortTh("가맹점명", "storeNm")}
                  {renderSortTh("구독명", "planNm")}
                  {renderSortTh("구독가격", "planPrice")}
                  {renderSortTh("점주명", "ownerNm")}
                  {renderSortTh("연락처", "ownerTelNo")}
                  {renderSortTh("업태/업종", "bizType")}
                  {renderSortTh("상태", "approvalStatus")}
                  <th>상세</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan="9" className="adminstoreinfo-empty-cell">로딩 중...</td></tr>
                ) : storeList.length === 0 ? (
                  <tr><td colSpan="9" className="adminstoreinfo-empty-cell">가맹점이 없습니다.</td></tr>
                ) : storeList.map((store) => (
                  <tr key={store.storeCode}>
                    <td>{store.storeCode}</td>
                    <td>{store.storeNm}</td>
                    <td>{store.planNm}</td>
                    <td>{store.planPrice}</td>
                    <td>{store.ownerNm}</td>
                    <td>{store.ownerTelNo}</td>
                    <td>{store.bizType}</td>
                    <td>{store.approvalStatus}</td>
                    <td>
                      <button
                        className="adminstoreinfo-btn adminstoreinfo-btn-detail"
                        onClick={() => handleOpenModal(store)}
                      >
                        보기
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      {isDetailOpen && selectedStore && (
        <StoreInfoModal
          storeCode={selectedStore.storeCode}
          onClose={handleCloseModal}
          isWithdrawal={activeTab === "WITHDRAWAL"}
          withdrawalRequestDateTime={selectedStore.withdrawalRequestTime}
        />
      )}
    </div>
  );
};

export default AdminStoreInfo;